type UnknownRecord = Record<string, unknown>;

const MAX_STRING_LENGTH = 4000;
const MAX_DEPTH = 3;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;

const toLatinSafe = (value: string) =>
  value.replace(/[\u0100-\uFFFF]/g, (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`);

const truncate = (value: string) => (value.length <= MAX_STRING_LENGTH ? value : `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`);

const safeRead = (source: unknown, key: string): unknown => {
  try {
    return (source as Record<string, unknown>)?.[key];
  } catch {
    return undefined;
  }
};

const normalizeValue = (value: unknown, depth: number, seen: WeakSet<object>): unknown => {
  if (value == null) return value;
  if (typeof value === 'string') return toLatinSafe(truncate(value));
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function') return `[Function ${(value as Function).name || 'anonymous'}]`;
  if (typeof value !== 'object') return toLatinSafe(String(value));

  if (seen.has(value)) return '[Circular]';
  if (depth >= MAX_DEPTH) return '[MaxDepth]';
  seen.add(value);

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => normalizeValue(item, depth + 1, seen));
  }

  const output: UnknownRecord = {};
  Object.getOwnPropertyNames(value)
    .slice(0, MAX_OBJECT_KEYS)
    .forEach((key) => {
      output[key] = normalizeValue(safeRead(value, key), depth + 1, seen);
    });

  return output;
};

export const getUnicoErrorDetailsForLog = (error: unknown): UnknownRecord => {
  const details: UnknownRecord = {};

  const knownFields = [
    'name',
    'message',
    'stack',
    'code',
    'errorCode',
    'type',
    'status',
    'statusCode',
    'line',
    'lineNumber',
    'column',
    'columnNumber',
    'fileName',
    'sourceURL',
  ];

  if (error instanceof Error) {
    details.name = error.name;
    details.message = error.message;
    details.stack = error.stack;
  } else if (typeof error === 'string') {
    details.message = error;
  } else if (typeof error === 'number' || typeof error === 'boolean') {
    details.message = String(error);
  }

  knownFields.forEach((field) => {
    const value = safeRead(error, field);
    if (value !== undefined && value !== null) details[field] = value;
  });

  if (error && typeof error === 'object') {
    Object.getOwnPropertyNames(error)
      .slice(0, MAX_OBJECT_KEYS)
      .forEach((key) => {
        if (details[key] !== undefined) return;
        details[key] = safeRead(error, key);
      });
  }

  const reason = safeRead(error, 'reason');
  if (reason !== undefined) details.reason = reason;
  const cause = safeRead(error, 'cause');
  if (cause !== undefined) details.cause = cause;

  return normalizeValue(details, 0, new WeakSet()) as UnknownRecord;
};

export const getUnicoErrorSearchText = (error: unknown): string => {
  const details = getUnicoErrorDetailsForLog(error);
  const parts = [
    details.name,
    details.message,
    details.code,
    details.errorCode,
    details.stack,
    details.fileName,
    details.sourceURL,
    details.reason,
    details.cause,
  ];
  return parts
    .map((value) => (typeof value === 'string' ? value : JSON.stringify(value ?? '')))
    .join(' ')
    .toLowerCase();
};
